import { combineReducers } from "redux";
import * as types from "./types";
import * as searchTypes from "../search/types";
import { USER_SIGNED_OUT } from "../user/types";
import { BOOK_LAUNCHED } from "../book/types";
import { removeDuplicates, removeItem } from "../../utils/array-utils";
import {
  ACTIVATE_ASSET_SUCCESS,
  RETURN_ASSET_SUCCESS,
  SYNC_ACTIVATE_ASSET_SUCCESS,
  SYNC_RETURN_ASSET_SUCCESS
} from "../bridge/types";

const booksByIsbnReducer = (state = {}, action) => {
  switch (action.type) {
    case types.GET_EXPIRED_BOOKS_SUCCESS:
      const expiredIsbns = action.expiredBooks.books.reduce((acc, book) => {
        acc[book.isbn] = book;
        return acc;
      }, {});
      return { ...state, ...expiredIsbns };
    case types.GET_EXPIRED_BOOKS_FAILURE:
      return { ...state, ...action.expiredBooks };
    case types.GET_BOOK_LIST_INTERMEDIATE_RESPONSE:
      const byIsbn = action.response.books.reduce((acc, book) => {
        acc[book.isbn] = book;
        return acc;
      }, {});
      return { ...state, ...byIsbn };
    case types.GET_BOOK_INFO_REQUEST:
      const newOnes = action.isbns.reduce((acc, isbn) => {
        acc[isbn] = { isLoading: true };
        return acc;
      }, {});
      return { ...state, ...newOnes };
    case types.GET_BOOK_INFO_SUCCESS:
      const bookUpdates = action.books.reduce((acc, book) => {
        acc[book.isbn] = book;
        return acc;
      }, {});
      return { ...state, ...bookUpdates };
    case SYNC_ACTIVATE_ASSET_SUCCESS:
      return { ...state, ...{ [action.vbid]: action.asset } };
    case searchTypes.SEARCH_BOOK_METADATA_SUCCESS:
      if (action.source === searchTypes.LICENSE_SOURCE) {
        const searchByIsbn = action.results.reduce((acc, book) => {
          acc[book.isbn] = book;
          return acc;
        }, {});
        return { ...state, ...searchByIsbn };
      } else {
        return state;
      }
    case types.REDEEM_CODE_SUCCESS:
      // TODO action.books can be added here to avoid license update
      // redeemed book response needs to have more data though
      return state;
    case USER_SIGNED_OUT:
      return {};
    case RETURN_ASSET_SUCCESS:
      return {
        ...state,
        [action.vbid]: {
          ...state[action.vbid],
          hasLicense: false
        }
      };
    case BOOK_LAUNCHED:
      const oldData = state[action.isbn];
      if (oldData) {
        return {
          ...state,
          [action.isbn]: {
            ...oldData,
            viewedAt: new Date().toISOString()
          }
        };
      }
      return state;
    default:
      return state;
  }
};

// See also persist.js
const expiredBooksReducer = (state = [], action) => {
  switch (action.type) {
    case types.GET_EXPIRED_BOOKS_SUCCESS:
      let allIsbns = action.expiredBooks.books.map(b => b.isbn);
      allIsbns = allIsbns.filter((item, pos) => {
        return allIsbns.indexOf(item) === pos;
      });
      return allIsbns;
    case types.GET_EXPIRED_BOOKS_FAILURE:
      return [];
    default:
      return state;
  }
};

const booksAllIsbnsReducer = (state = [], action) => {
  switch (action.type) {
    case types.GET_BOOK_LIST_REQUEST:
      return [];
    case types.GET_BOOK_LIST_INTERMEDIATE_RESPONSE:
      return removeDuplicates(
        state.concat(action.response.books.map(({ isbn }) => isbn))
      );
    case types.REDEEM_CODE_SUCCESS:
      // TODO action.books can be added here to avoid license update
      // redeemed book response needs to have more data though
      return state;
    case USER_SIGNED_OUT:
      return [];
    case ACTIVATE_ASSET_SUCCESS:
    case SYNC_ACTIVATE_ASSET_SUCCESS:
      // Plain activations with no launch will move the book to the
      // front of the jigsaw-fetched book list, so we'll do the same
      // to keep things synced up
      return [action.vbid, ...removeItem(state, action.vbid)];
    case RETURN_ASSET_SUCCESS:
    case SYNC_RETURN_ASSET_SUCCESS:
      return [...removeItem(state, action.vbid)];
    case BOOK_LAUNCHED:
      if (state.indexOf(action.isbn) !== -1) {
        return [action.isbn, ...removeItem(state, action.isbn)];
      }
      return state;
    default:
      return state;
  }
};

const totalBooksReducer = (state = 0, action) => {
  switch (action.type) {
    case types.GET_BOOK_LIST_REQUEST:
      return 0;
    case types.GET_BOOK_LIST_INTERMEDIATE_RESPONSE:
      // This should always win out as an accurate total
      // even if we've activated assets prior to fetch or
      // between intermediate results returns
      return action.response.total;
    case types.GET_BOOK_LIST_SUCCESS:
      return action.total;
    case USER_SIGNED_OUT:
      return 0;
    default:
      return state;
  }
};

// See also persist.js
const expiredLoadStatus = (state = "waiting", action) => {
  switch (action.type) {
    case types.GET_EXPIRED_BOOKS_REQUEST:
      return "fetching";
    case types.GET_EXPIRED_BOOKS_SUCCESS:
    case types.GET_EXPIRED_BOOKS_FAILURE:
      return "done";
    default:
      return state;
  }
};

const isLoadingReducer = (state = false, action) => {
  switch (action.type) {
    case types.GET_BOOK_LIST_REQUEST:
      return true;
    case types.GET_BOOK_LIST_SUCCESS:
      return false;
    case USER_SIGNED_OUT:
      return false;
    default:
      return state;
  }
};

const finishedLoading = (state = false, action) => {
  switch (action.type) {
    case types.GET_BOOK_LIST_REQUEST:
      return false;
    case types.GET_BOOK_LIST_SUCCESS:
      return true;
    case USER_SIGNED_OUT:
      return false;
    default:
      return state;
  }
};

export default combineReducers({
  isLoading: isLoadingReducer,
  finishedLoading: finishedLoading,
  expiredLoadStatus,
  books: combineReducers({
    byIsbn: booksByIsbnReducer,
    allIsbns: booksAllIsbnsReducer,
    allExpiredIsbns: expiredBooksReducer
  }),
  totalBooks: totalBooksReducer
});
